import { Form } from "@components";
import { DialogTitle, Grid, Typography } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import { makeStyles } from "@material-ui/core/styles";
import React, { useCallback, useState } from "react";

const useStyles = makeStyles({
  textArea: {
    borderRadius: "0px",
  },
  divider: {
    marginLeft: "10px",
  },
  dialogActions: {
    "& button": {
      marginLeft: "5px",
    },
  },
  alert: {
    borderLeft: "3px solid #FFCC01",
    paddingLeft: "8px",
  },
});

interface Props {
  isOpen: boolean;
  closeModal: () => void;
  buttonText:
    | "APPROVE REQUEST"
    | "REJECT REQUEST"
    | "SEND BACK REQUEST"
    | "CANCEL REQUEST";
  successMessage?:
    | "Successfully Approved"
    | "Successfully Rejected"
    | "Successfully Sent Back"
    | "Successfully Cancelled";
  onAction?: (
    comments: string
  ) => Promise<{ success: boolean; error?: string }>;
  title?: string;
  required?: boolean;
  isAction?: boolean;
}

type Remarks = {
  remarks: string | undefined;
};

type Key = keyof Remarks;

function validate(
  [name, value]: [string, any],
  required: boolean
): Promise<[Key, Error?]> {
  switch (name) {
    case "remarks":
      if (required) {
        return Promise.resolve([name as Key, Form.Validators.hasValue(value)]);
      } else {
        return Promise.resolve([name as Key]);
      }
    default:
      return Promise.resolve([name as Key]);
  }
}

export const Modal: React.FC<Props> = ({
  isOpen,
  closeModal = () => {},
  buttonText,
  onAction,
  successMessage,
  title = "Approve Request",
  required = false,
  isAction = false,
}) => {
  const classNames = useStyles();
  const [error, setError] = useState<boolean>(false);
  const [actionPerformed, setActionPerformed] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>(
    "There was an error"
  );
  const [isSubmitting, setSubmittingState] = useState<boolean>(false);

  const form = Form.useManager<Remarks>({
    remarks: undefined,
  });

  function onChange(name: Key, value: any): void {
    form.set(Form.target.VALUE, [name, value]);
    validate([name, value], required).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
    });
  }

  const onCleanState = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    closeModal();
    setError(false);
    setActionPerformed(false);
  };

  const onSubmit = useCallback(
    (event) => {
      Promise.all(
        Object.entries(form.values).map(([name, value]: [string, any]) =>
          validate([name, value], required)
        )
      ).then((validations) => {
        const valid = validations.reduce((valid, [name, error]) => {
          return valid && !error;
        }, true);

        if (valid) {
          const comments = form.values.remarks ? form.values.remarks : "";
          setSubmittingState(true);
          !!onAction &&
            onAction(comments).then(({ success, error }) => {
              setError(!success);
              !!error && setErrorMessage(error);
              setActionPerformed(true);
            });
        } else {
          validations.forEach(([name, error]) => {
            form.set(Form.target.ERROR, [name, error]);
          });
        }
      });
    },
    [form, onAction, required]
  );

  let content = null;

  if (actionPerformed) {
    content = (
      <Box m={10}>
        <Grid
          container
          direction="column"
          alignItems="center"
          justify="center"
          spacing={5}
        >
          <Grid item>
            <Typography variant="body1">
              {error ? errorMessage : successMessage}
            </Typography>
          </Grid>
          <Grid item>
            <Button onClick={onCleanState} color="primary" variant="outlined">
              DISMISS
            </Button>
          </Grid>
        </Grid>
      </Box>
    );
  } else {
    content = (
      <React.Fragment>
        <DialogTitle>
          <Typography variant="body1">{title}</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph className={classNames.alert}>
            Remarks (max 200 characters)
          </Typography>
          <Box>
            <Form>
              <Form.Textarea
                name="remarks"
                onChange={onChange}
                size={12}
                maxLength={200}
                value={form.values.remarks}
                required={required}
                error={form.errors.remarks}
                rows={6}
              ></Form.Textarea>
            </Form>
          </Box>
        </DialogContent>
        <Box p={2} className={classNames.dialogActions}>
          <Button
            type="button"
            onClick={onSubmit}
            color="primary"
            variant="contained"
            disabled={isSubmitting}
          >
            {buttonText}
          </Button>
          <Button onClick={closeModal} color="primary" variant="outlined">
            CANCEL
          </Button>
        </Box>
      </React.Fragment>
    );
  }

  return <Dialog open={isOpen}>{content}</Dialog>;
};
